import { SportslineWebComponent_FreeExpertPickQuery } from '@lib/graphql-types';
import { Component, Host, Prop, h } from '@stencil/core';
import { SportslineExpertProps } from 'src/types';

@Component({
  tag: 'sportsline-header-expert',
  styleUrl: 'sportsline-expert.css',
  shadow: true,
})
export class SportslineExpertInformation {
  /**
   * The expert information to be displayed in the card.
   * This prop expects an object adhering to the `SportslineExpertProps` interface,
   * which contains details such as the expert's name, headshot URL, and performance streaks.
   */
  @Prop() expert: SportslineExpertProps;

  /**
   * The expert streaks information to be displayed in the card.
   * This prop expects an array of objects, each containing details about the expert's performance streaks.
   */
  @Prop() expertStreaks?: SportslineWebComponent_FreeExpertPickQuery['freeExpertPick']['expertStreaks'];

  private get getExpertName() {
    return `${this.expert.firstName} ${this.expert.lastName}`;
  }

  private get getStreak() {
    return this.expertStreaks?.[0];
  }

  private get getRanking() {
    return Boolean(this.getStreak?.rank) ? `#${this.getStreak.rank} ${this.getStreak.league.abbrev} EXPERT` : '';
  }

  private get getStreakStats() {
    const { profit, won, lost, pushed, winPercentage } = this.getStreak;
    const hasPush = pushed > 0;

    const record = hasPush ? `${won}-${lost}-${pushed}` : `${won}-${lost}`;

    // Profit will always be positive
    return `+${profit}, ${record} (${winPercentage}%) `;
  }

  private get getFormattedStreakLabel() {
    return `in ${this.getStreak.label} Picks`;
  }

  render() {
    if (!Boolean(this.expert)) {
      return null;
    }

    return (
      <Host>
        <div class="sportsline-expert">
          <div class="sportsline-expert__box">
            <sportsline-expert-avatar src={this.expert.clearHeadshotUrl} alt={this.getExpertName} />
            <div class="sportsline-expert__info">
              <span class="sportsline-expert__name">{this.getExpertName}</span>
              {this.getRanking && (
                <div class="sportsline-expert__ranking">
                  <sportsline-league-logo leagueId={this.getStreak.league.id} size="small" />
                  <span class="sportsline-expert__ranking-number">{this.getRanking}</span>
                </div>
              )}
            </div>
          </div>
          {Boolean(this.getStreak) && (
            <div class="sportsline-expert__streak">
              {this.getStreak.hot && <span>🔥</span>}
              <span class="sportsline-expert__streak-label">
                <span class="sportsline-expert__streak-label-stats">{this.getStreakStats}</span>
                {this.getFormattedStreakLabel}
              </span>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
